import { Chip, Tooltip } from '@mui/material';
import { FC } from 'react';
import { useRecordContext } from 'react-admin';

interface SshKeyBadgeProps {
  source?: string;
  label?: string;
}

const SshKeyBadge: FC<SshKeyBadgeProps> = ({ source = 'ssh_key_state' }) => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  const state = record[source];

  // Define badge properties based on state
  const getBadgeConfig = (keyState: string | null | undefined) => {
    switch (keyState) {
      case 'valid':
        return {
          label: 'Valid',
          color: '#10B981' as const, // green
          backgroundColor: '#D1FAE5',
          tooltip: 'SSH key is valid and active',
        };
      case 'expired':
        return {
          label: 'Expired',
          color: '#F59E0B' as const, // orange
          backgroundColor: '#FEF3C7',
          tooltip: 'SSH key has expired',
        };
      case 'revoked':
        return {
          label: 'Revoked',
          color: '#EF4444' as const, // red
          backgroundColor: '#FEE2E2',
          tooltip: 'SSH key has been revoked by an administrator',
        };
      case 'empty':
      case null:
      case undefined:
        return {
          label: 'No Key',
          color: '#6B7280' as const, // gray
          backgroundColor: '#F3F4F6',
          tooltip: 'No SSH key registered for this device',
        };
      default:
        return {
          label: 'Unknown',
          color: '#6B7280' as const,
          backgroundColor: '#F3F4F6',
          tooltip: 'Unknown SSH key state',
        };
    }
  };

  const config = getBadgeConfig(state);

  // Build detailed tooltip content
  const getTooltipContent = () => {
    const parts = [config.tooltip];

    if (record.ssh_key_registered_at) {
      parts.push(`Registered: ${new Date(record.ssh_key_registered_at).toLocaleString()}`);
    }

    if (record.ssh_key_expired_at) {
      parts.push(`Expired: ${new Date(record.ssh_key_expired_at).toLocaleString()}`);
    }

    if (record.ssh_key_last_used_at) {
      parts.push(`Last used: ${new Date(record.ssh_key_last_used_at).toLocaleString()}`);
    }

    return parts.join('\n');
  };

  return (
    <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{getTooltipContent()}</div>} arrow>
      <Chip
        label={config.label}
        size="small"
        sx={{
          'backgroundColor': config.backgroundColor,
          'color': config.color,
          'fontWeight': 600,
          'fontSize': '0.75rem',
          'height': '24px',
          'cursor': 'help',
          '& .MuiChip-label': {
            px: 1.5,
          },
        }}
      />
    </Tooltip>
  );
};

export default SshKeyBadge;
